<script>import {
  cfgBrandTitle,
  FeatureSet,
  formatBaseCurrency,
  ReleaseStage,
  stateFeatureSet,
  stateTheme,
  stateUi,
  wantAtLeastReleaseStage
} from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { isMobile, locale } from "@infinitro/global";
import { GradientBorder } from "@infinitro/info-card";
import { Text } from "@infinitro/text";
import { onMount } from "svelte";
import { get } from "svelte/store";
import Swiper from "swiper";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/swiper-bundle.css";
import { makeBanners } from "../data";
import { stateFormMode } from "../state";
import { FormMode } from "../types";
const BRAND_NAME = cfgBrandTitle();
const AMOUNT_OF_LEVEL_REWARDS = 17e5;
const NUMBER_OF_DEPOSITS = 3;
const PERCENTAGE = 275;
const bannerVars = {
  BRAND: BRAND_NAME,
  NUMBER_OF_DEPOSITS,
  PERCENTAGE,
  AMOUNT_OF_LEVEL_REWARDS: formatBaseCurrency(AMOUNT_OF_LEVEL_REWARDS, true)
};
let mounted = false;
let hasBanners = false;
let swiper;
let swiperContainer;
let banners = [];
$: useFlat = $stateUi.modal.modalStyle === "flat";
$: useGlass = $stateUi.modal.modalStyle === "glass";
$: useTinted = $stateUi.modal.modalStyle === "tinted";
$: banners = makeBanners($stateTheme, $stateFeatureSet, $locale);
const isBannerVisible = (b) => {
  const allowPreviewAlpha = wantAtLeastReleaseStage(ReleaseStage.Alpha) && $stateFeatureSet === FeatureSet.Preview;
  const showForMode = !b.showOnly || b.showOnly === "register" && $stateFormMode === FormMode.Register || b.showOnly === "login" && $stateFormMode === FormMode.Login;
  return (b.show || allowPreviewAlpha) && showForMode;
};
$: hasBanners = banners.some(isBannerVisible);
const formatPlaceholders = (text, vars) => {
  if (!text) return "";
  return text.replace(/\[([A-Z0-9_]+)\]/g, (match, key) => {
    const v = vars[key];
    if (v === void 0 || v === null) return match;
    if (key === "AMOUNT_OF_LEVEL_REWARDS" && typeof v === "number") return formatBaseCurrency(v);
    return String(v);
  });
};
const applyBracketHighlight = (str) => {
  if (!str) return "";
  if (useGlass) return str;
  return str.replace(/\[([^\]]+)\]/g, '<span style="color: var(--color-highlight-1)">$1</span>');
};
const bannerVerbiage = (key, localeCode) => {
  if (!key) return "";
  return get(t)(key) ?? "";
};
const authBannerTitleHtml = (titleKey, localeCode) => {
  const translated = bannerVerbiage(titleKey, localeCode);
  return applyBracketHighlight(formatPlaceholders(translated, bannerVars).toUpperCase());
};
const authBannerDescriptionHtml = (descriptionKey, localeCode) => {
  const translated = bannerVerbiage(descriptionKey, localeCode);
  return formatPlaceholders(translated, bannerVars).split("\n").map((line) => applyBracketHighlight(line)).join("<br />");
};
const imgFallbackOnce = (fallback) => (e) => {
  const img = e.currentTarget;
  if (img) img.onerror = null;
  if (fallback && img) img.src = fallback;
};
const initializeSwiper = () => {
  if (swiper) swiper.destroy(true, true);
  const paginationEl = swiperContainer?.querySelector(".swiper-pagination") ?? void 0;
  swiper = new Swiper(swiperContainer, {
    modules: [Autoplay, Pagination],
    loop: true,
    loopAddBlankSlides: false,
    slidesPerView: 1,
    autoplay: { delay: 5e3, disableOnInteraction: false },
    pagination: {
      el: paginationEl ?? ".swiper-pagination",
      clickable: true,
      type: "bullets"
    }
  });
};
$: if (swiperContainer && mounted && hasBanners && $stateFormMode) initializeSwiper();
onMount(() => {
  mounted = true;
});
</script>

{#if hasBanners && !useTinted}
	<div class="swiper auth-banners-container" bind:this={swiperContainer}>
		<div class="swiper-wrapper">
			{#each banners as item}
				{#if (item.show || (wantAtLeastReleaseStage(ReleaseStage.Alpha) && $stateFeatureSet === FeatureSet.Preview)) && (!item.showOnly || (item.showOnly === 'register' && $stateFormMode === FormMode.Register) || (item.showOnly === 'login' && $stateFormMode === FormMode.Login))}
					<div class="swiper-slide">
						<GradientBorder radius={16} padding={1}>
							<div class="banner-frame">
								<img
									src={item.img}
									alt={$t(Verbiage.banner_alt, 'alts')}
									class="swiper-banner"
									class:swiper-banner-flat={useFlat || useGlass || useTinted}
									on:error={imgFallbackOnce(item.fallbackImg)}
								/>

								<img
									src={item.imgMobile}
									alt={$t(Verbiage.banner_alt, 'alts')}
									class="swiper-banner-mobile"
									class:swiper-banner-flat={useFlat || useGlass || useTinted}
									on:error={imgFallbackOnce(item.fallbackImgMobile)}
								/>

								{#if item.title && item.description}
									<div class="banner-text-container" class:banner-text-container-glass={useGlass}>
										<Text
											type="title"
											titleSize="h5"
											titleWeight={useGlass && !$isMobile ? 'bold' : 'extrabold'}
											customStyle="line-height: {useGlass ? '1' : '1.2'}; font-size: {useGlass ||
											$isMobile
												? ''
												: '36px'};"
										>
											{@html authBannerTitleHtml(item.title, $locale)}
										</Text>
										<Text
											bodySize="large"
											bodyWeight={'regular'}
											customStyle="width: {$isMobile
												? '50%'
												: useGlass
													? '70%'
													: '35%'}; letter-spacing: {useGlass
												? '0.03em'
												: '0'}; line-height: {useGlass ? '1.2' : '1.5'};"
										>
											{@html authBannerDescriptionHtml(item.description, $locale)}
										</Text>
									</div>
								{/if}
							</div>
						</GradientBorder>
					</div>
				{/if}
			{/each}
		</div>
		{#if banners.length > 1}
			<div class="swiper-controls-container">
				<div class="swiper-controls">
					<div class="swiper-pagination pagination-override" />
				</div>
			</div>
		{/if}
	</div>
{/if}

<style>
	.banner-frame {

		position: relative;

		width: 100%;

		overflow: hidden;

		border-radius: 16px;
}

	.banner-frame :global(img) {

		display: block;

		height: auto;

		width: 100%;
}

	.banner-text-container {

		position: absolute;

		inset: 0px;

		display: flex;

		max-height: 100%;

		width: 100%;

		flex-direction: column;

		align-items: flex-start;

		justify-content: center;

		overflow: hidden;

		text-align: start;

		padding: clamp(12px, 4vw, 32px);
}

	.banner-text-container-glass {

		max-width: 62%;

		gap: 0.5rem;
}

	.auth-banners-container {

		margin-bottom: 0.5rem;

		min-height: 180px;

		width: 100%;

		overflow: hidden;
}

	@media (min-width: 1024px) {

		.auth-banners-container {

				margin-bottom: 0px;
		}
}

	.auth-banners-container .swiper-slide {

		position: relative;

		height: auto;

		width: 100%;
}

	.auth-banners-container .swiper-slide img {

		height: auto;

		width: 100%;
}

	.swiper-controls-container {

		height: 30px;
}

	:global(.auth-banners-container .swiper-pagination-bullet) {

		height: 6px;

		width: 6px;

		--tw-bg-opacity: 1;

		background-color: rgb(255 255 255 / var(--tw-bg-opacity));

		opacity: 0.9;
		background-color: var(--color-neutral-100) !important;
}

	:global(.auth-banners-container .swiper-pagination-bullet-active) {

		height: 6px;

		opacity: 1;
		width: 24px !important;
		border-radius: 25px !important;
		background-color: var(--color-neutral-100);
		position: relative;
		overflow: visible;
}

	:global(.auth-banners-container .swiper-pagination-bullet-active::before) {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		height: 100%;
		width: 0;
		background-image: linear-gradient(
			180deg,
			var(--color-primary-500) 0%,
			var(--color-primary-500) 100%
		);
		animation: fillProgress 5s linear forwards;
		border-radius: 25px;
		box-shadow: 0px 0px 10px 0px var(--color-primary-500);
		z-index: 1;
	}

	:global(.auth-banners-container .swiper-pagination-bullet-active::after) {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		height: 100%;
		width: 100%;
		border-radius: 25px;
		background-color: transparent;
		z-index: 2;
	}

	@keyframes fillProgress {
		0% {
			width: 0;
		}
		100% {
			width: 100%;
		}
	}

	.swiper-banner {

		display: none;

		max-width: 100%;

		border-radius: 16px;
}

	@media (min-width: 1024px) {

		.swiper-banner {

				display: block;
		}
}

	.swiper-banner-mobile {

		display: block;

		border-radius: 16px;
}

	@media (min-width: 1024px) {

		.swiper-banner-mobile {

				display: none;
		}
}
</style>
